import { Alert } from '@mui/material';
import { useFormik } from 'formik';
import { useContext } from 'react';
import { object, ObjectSchema, string } from 'yup';
import QuestionsApi from '../../../../../../apis/QuestionsApi';
import FormikRadioGroup from '../../../../../../components/FormikRadioGroup/FormikRadioGroup';
import SnackbarContext from '../../../../../../contexts/SnackbarContext';
import { MultipleChoiceAnswerDto } from '../../../../../../generated-types/multipleChoiceAnswerDto';
import useApi from '../../../../../../lib/useApi';
import { MultipleChoiceQuestionWithDis } from '../../../../../../types/QuestionAndAnswersWithDiscriminator';
import QuestionCardFooter from './QuestionCardFooter';

/**
 * The properties for the multiple choice question component
 */
interface MultipleChoiceQuestionProps extends BaseQuestionProps {
  /**
   * The multiple choice question to show, with the type discriminator
   */
  question: MultipleChoiceQuestionWithDis;
}

/**
 * The multiple choice question component, to be displayed within a card within the quiz module
 */
const MultipleChoiceQuestion = (props: MultipleChoiceQuestionProps) => {
  const questionsApi = useApi(QuestionsApi);
  const snackbar = useContext(SnackbarContext);
  const schema: ObjectSchema<MultipleChoiceAnswerDto> = object({
    answer: string().required(),
  });

  /**
   * The formik form for the multiple choice question
   */
  const formik = useFormik<MultipleChoiceAnswerDto>({
    initialValues: {
      answer: '',
    },
    validationSchema: schema,
    onSubmit: (val, { setSubmitting, resetForm }) => {
      const answerWithDiscriminator = {
        $type: props.question.$type, // Preserve the original type - must occur first
        ...val,
      };
      questionsApi
        .AnswerQuestion(props.question.questionId, answerWithDiscriminator)
        .then(props.handlePostAnswer)
        .catch(() => {
          snackbar.setAlert(
            <Alert variant="filled" severity="error">
              An error occurred while submitting the answer. Please try again.
              If this continues, contact us.
            </Alert>
          );
          snackbar.triggerSnackbar();
        })
        .finally(() => {
          setSubmitting(false);
          resetForm();
        });
    },
  });
  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikRadioGroup
        dataTestId="multiple-choice-question-id"
        formik={formik}
        name="answer"
        options={props.question.possibleAnswers.reduce(
          (acc: Record<string, string>, cur: string) => {
            acc[cur] = cur;
            return acc;
          },
          {} as Record<string, string>
        )}
      />
      <QuestionCardFooter
        isLoading={props.isLoading || formik.isSubmitting}
        selectedAnswer={formik.values.answer != ''}
        completedQuestions={props.completedQuestions}
        questionCountMinimum={props.questionCountMinimum}
      />
    </form>
  );
};

export default MultipleChoiceQuestion;
